import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, Star } from "lucide-react";

import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import EventCard from "@/components/EventCard";
import Counter from "@/components/Counter";
import GoldMarquee from "@/components/GoldMarquee";
import api, { getWithRetry } from "@/lib/api";
import { IMAGES, HOOKAH_SPECIALS, HAPPY_HOUR, STATS, BRAND } from "@/lib/content";
import { toast } from "sonner";

const ROTATE = ["HOOKAH", "COCKTAILS", "NIGHTLIFE", "VIP"];

function Hero() {
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI((v) => (v + 1) % ROTATE.length), 2200); return () => clearInterval(id); }, []);
  return (
    <section className="relative pt-32 md:pt-40 pb-10">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <div className="text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex justify-center mb-5">
            <Link to="/events" data-testid="hero-homecoming" className="group inline-flex items-center gap-2.5 sm:gap-3 rounded-full bg-neutral-900 pl-2 pr-4 sm:pr-5 py-2 hover:bg-neutral-800 transition-colors max-w-full">
              <span className="rounded-full bg-white text-neutral-900 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide px-3 py-1 whitespace-nowrap">Howard Homecoming</span>
              <span className="text-white/90 text-xs sm:text-sm truncate">Oct 11–18 · Homecoming specials & deals all week</span>
              <ArrowRight size={15} className="text-white shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }} className="pill-badge"><span className="dot" /> Washington DC · U Street</motion.span>
          <h1 className="display-mega mt-6 sm:mt-8 text-[13vw] sm:text-[12vw] lg:text-[9.5rem] leading-[0.86] px-1 break-words">
            <span className="block">PREMIER</span>
            <span className="block h-[0.9em] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span key={ROTATE[i]} initial={{ y: "100%" }} animate={{ y: "0%" }} exit={{ y: "-100%" }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="block text-accent">{ROTATE[i]}</motion.span>
              </AnimatePresence>
            </span>
            <span className="block">LOUNGE</span>
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.6 }} className="text-neutral-600 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mt-6 sm:mt-8 px-2">
            Premium hookah, craft cocktails, happy hour, VIP bottle service & live DJs — Washington DC's premier hookah lounge.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }} className="flex flex-wrap gap-3 justify-center mt-9">
            <Link to="/reservations" data-testid="hero-reserve" className="btn-dark">Reserve a Table <ArrowRight size={17} /></Link>
            <Link to="/buy-tickets" data-testid="hero-tickets" className="btn-gold">Reserve <ArrowRight size={16} /></Link>
            <Link to="/menus" data-testid="hero-hookah" className="btn-ghost">Order Hookah</Link>
          </motion.div>
        </div>

        <Reveal delay={0.2} className="mt-14">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[21/9]">
            <img src={IMAGES.hookah1} alt="ChiCha Lounge hookah experience in Washington DC" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <span className="pill-badge !bg-white/85 !text-neutral-900 mb-3">Now Open</span>
                <div className="display-mega text-white text-3xl md:text-5xl">Luxury meets nightlife.</div>
              </div>
              <div className="glass rounded-2xl px-4 py-3 flex items-center gap-2"><Star size={16} className="text-neutral-900 fill-neutral-900" /><span className="text-sm text-neutral-900 font-semibold">4.5 · Google</span></div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="py-8">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {STATS.map((s, idx) => (
          <Reveal key={s.label} delay={idx * 0.08} className="card-lux px-6 py-7 text-center">
            <div className="display-mega text-4xl md:text-5xl text-accent"><Counter value={s.value} suffix={s.suffix} /></div>
            <div className="text-neutral-500 text-xs uppercase tracking-[0.16em] mt-2">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Events({ events }) {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <SectionHeading overline="What's On" title={<>The<br/>line-up</>} subtitle="Live DJs, fight nights, bottomless brunch and bottle service. Reserve before the doors fill up." />
          <Reveal><Link to="/events" data-testid="all-events" className="btn-ghost shrink-0">All Events <ArrowUpRight size={16} /></Link></Reveal>
        </div>
        {events.length === 0 ? <p className="text-neutral-400">Loading events…</p> : (
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 3).map((ev, idx) => <StaggerItem key={ev.id}><EventCard ev={ev} showCountdown={idx === 0} /></StaggerItem>)}
          </Stagger>
        )}
      </div>
    </section>
  );
}

function ImageBlock({ overline, title, text, cta, to, img, reverse }) {
  return (
    <section className="py-10">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <div className={`grid lg:grid-cols-2 gap-8 items-stretch ${reverse ? "" : ""}`}>
          <Reveal className={`relative rounded-[2rem] overflow-hidden min-h-[360px] ${reverse ? "lg:order-2" : ""}`}>
            <img src={img} alt={title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          </Reveal>
          <Reveal delay={0.1} className={`card-lux p-9 md:p-12 flex flex-col justify-center ${reverse ? "lg:order-1" : ""}`}>
            <span className="overline">{overline}</span>
            <h3 className="display-mega text-3xl md:text-5xl mt-4">{title}</h3>
            <p className="text-neutral-500 text-lg mt-5 leading-relaxed">{text}</p>
            <Link to={to} className="btn-dark mt-8 self-start">{cta} <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HappyHour() {
  return (
    <section className="py-16">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <div className="card-lux p-8 md:p-12 grid lg:grid-cols-2 gap-10">
          <div>
            <span className="overline">5PM – 9PM Daily</span>
            <h3 className="display-mega text-4xl md:text-5xl mt-4">Happy hour on U Street</h3>
            <p className="text-neutral-500 text-lg mt-5">$10 off house cocktails, discounted hookah and small plates.</p>
            <div className="grid grid-cols-2 gap-3 mt-8">
              {HAPPY_HOUR.map((h) => (
                <div key={h.name} className="flex items-center justify-between rounded-2xl bg-black/[0.03] px-5 py-4">
                  <span className="text-neutral-700 text-sm">{h.name}</span>
                  <span className="display-mega text-lg text-accent">{h.price}</span>
                </div>
              ))}
            </div>
            <Link to="/reservations" data-testid="happyhour-reserve" className="btn-gold mt-8">Reserve Now <ArrowRight size={16} /></Link>
          </div>
          <div className="rounded-2xl bg-black/[0.03] p-7">
            <span className="overline">Hookah Specials</span>
            <div className="mt-6 space-y-4">
              {HOOKAH_SPECIALS.map((s) => (
                <div key={s.label} className="flex items-center justify-between border-b border-black/10 pb-4 last:border-0">
                  <span className="text-neutral-600">{s.label}</span>
                  <span className="display-mega text-2xl text-accent">{s.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GalleryStrip({ items }) {
  if (!items.length) return null;
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <SectionHeading overline="The Moments" title="Life at ChiCha" />
          <Reveal><Link to="/gallery" data-testid="all-gallery" className="btn-ghost shrink-0">Full Gallery <ArrowUpRight size={16} /></Link></Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.slice(0, 8).map((g, idx) => (
            <Reveal key={g.id} delay={(idx % 4) * 0.05} className={`relative overflow-hidden rounded-[1.5rem] group ${idx % 5 === 0 ? "md:row-span-2 aspect-[3/4] md:aspect-auto" : "aspect-square"}`}>
              <img src={g.image_url} alt={g.caption || "ChiCha Lounge DC"} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ items }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { if (!items.length) return; const id = setInterval(() => setIdx((v) => (v + 1) % items.length), 5000); return () => clearInterval(id); }, [items.length]);
  if (!items.length) return null;
  const t = items[idx];
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
        <span className="overline">Google Reviews</span>
        <motion.p key={idx} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="display-mega text-2xl md:text-4xl mt-6 normal-case leading-tight">"{t.text}"</motion.p>
        <div className="flex justify-center gap-1 mt-7">{Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={16} className="text-[#0B0B0D] fill-[#0B0B0D]" />)}</div>
        <p className="text-neutral-500 mt-3 uppercase tracking-[0.16em] text-xs">{t.name} · {t.source}</p>
        <div className="flex justify-center gap-2 mt-7">{items.map((_, k) => <button key={k} data-testid={`testimonial-dot-${k}`} onClick={() => setIdx(k)} className={`h-1.5 rounded-full transition-all ${k === idx ? "w-8 bg-[#0B0B0D]" : "w-1.5 bg-black/20"}`} />)}</div>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); if (!email) return; setLoading(true);
    try { await api.post("/newsletter", { email, source: "home" }); toast.success("You're on the list."); setEmail(""); }
    catch { toast.error("Could not subscribe."); } finally { setLoading(false); }
  };
  return (
    <section className="py-16">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <div className="card-lux px-8 md:px-16 py-16 text-center">
          <h2 className="display-mega text-4xl md:text-6xl">Be first to know<br/>what's next</h2>
          <p className="text-neutral-500 text-lg mt-5 max-w-xl mx-auto">Event drops, VIP table offers and birthday-club perks — straight to your inbox.</p>
          <form onSubmit={submit} className="flex items-center bg-neutral-100 rounded-full overflow-hidden pl-5 pr-1.5 py-1.5 max-w-md mx-auto mt-8">
            <input data-testid="home-newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="bg-transparent flex-1 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none py-2" />
            <button data-testid="home-newsletter-submit" disabled={loading} className="btn-gold !py-2.5 !px-5 text-sm shrink-0">Notify Me <ArrowRight size={15} /></button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    getWithRetry("/events?status=upcoming").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);
  return (
    <>
      <Seo />
      <Hero />
      <GoldMarquee items={["Premium Hookah", "Craft Cocktails", "VIP Bottle Service", "Live DJs", "Late Night", "U Street DC"]} />
      <Stats />
      <Events events={events} />
      <ImageBlock overline="The Experience" title="Premium hookah & house mixes" text="20+ flavors and custom house mixes crafted by our specialists, in a lounge built for slow, luxurious nights." cta="Explore Menus" to="/menus" img={IMAGES.hookah2} />
      <ImageBlock overline="Bottle Service" title="VIP tables done right" text="Reserved sections, premium spirits and champagne with white-glove hospitality — the ultimate ChiCha night." cta="View VIP Tables" to="/vip-tables" img={IMAGES.bottle} reverse />
      <HappyHour />
      <GalleryStrip items={gallery} />
      <Testimonials items={testimonials} />
      <ImageBlock overline="Private Events" title="Host your celebration" text="Birthdays, corporate mixers, graduations and bachelor parties — reserved sections and full buyouts crafted around you." cta="Request a Quote" to="/private-events" img={IMAGES.crowd} />
      <Newsletter />
    </>
  );
}
